import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCreateOrUpdateRebateGroup } from '@pages/IntroducingBroker/Rebates/hooks/useCreateOrUpdateRebateGroup'
import { useIBRebateGroupsSearch } from '@pages/IntroducingBroker/Rebates/hooks/useIBRebateGroupsSearch'
import { useRebateGroupsOptions } from '@pages/IntroducingBroker/Rebates/hooks/useRebateGroupsOptions'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { FileIcon } from '@icons/FileIcon'

import { PageSection } from '@ui/v2/PageSection/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import { FilterOptions, TableFilter } from '@ui/v2/TableFilter/TableFilter'

import { PageQuery, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { IntroducingBrokerRebateGroupDto } from '@model/IntroducingBrokerRebateGroupDto'
import { NameDto } from '@model/NameDto'

import { Loading } from '@global/Loading/Loading'
import { FilterQueryProps } from '@global/filter/FilterQueryModal'
import { Modal } from '@global/modal/Modal'

import { useCallbackWithForceRefresh } from '@hooks/useCallbackWithForceRefresh'
import { IAppendableFetchResult, useFetchAppendablePage, useFetchOne } from '@hooks/useFetch'
import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { HeaderActions } from '../Common/HeaderActions'
import { GroupFormValues, GroupModal } from '../GroupModal'
import { RebateOptionsModal } from '../RebateOptionsModal'
import { RebatesSummary, SummaryItem } from '../RebatesSummary'
import { DeleteGroupModal } from './DeleteGroupModal'
import { EmptyGroupsPageTable, GroupsPageTable } from './GroupsPageTable'

import styles from './GroupsPage.module.scss'

export const IBRebatesGroupsPage: FC = () => {
  const { t } = useTranslation()

  const [groupModalVisible, setGroupModalVisible] = useState(false)
  const [isFilterVisible, setIsFilterVisible] = useState(false)
  const [deleteGroupId, setDeleteGroupId] = useState<string>()
  const [selectedGroup, setSelectedGroup] = useState<Partial<IntroducingBrokerRebateGroupDto>>()
  const [isRebateApprovalTypeModalOpen, setRebateApprovalTypeModalOpen] = useState(false)
  const [isRebatePeriodModalOpen, setRebatePeriodModalOpen] = useState(false)
  const [isCreatingNewGroup, setIsCreatingNewGroup] = useState(false)

  const {
    query: tableQuery,
    sortOptions: tableSortOptions,
    search: tableSearch,
    setSearch: setTableSearch,
    forceRefresh: forceRefreshTable,
  } = useIBRebateGroups()

  const isAnyFilterActive = useMemo(
    () => Object.values(tableSearch || {}).some((value) => !!value),
    [tableSearch]
  )

  const { groupOptions, setSearch: setListSearch } = useIBRebateGroupsSearch()

  const { rebateApprovalTypes, rebatePeriods } = useRebateGroupsOptions()

  const {
    summaryItems,
    forceRefresh: forceRefreshGroupsSummary,
    isLoading: isSummaryLoading,
  } = useIBRebateGroupsSummary(tableSearch)

  const refreshData = () => {
    forceRefreshTable()
    forceRefreshGroupsSummary()
  }

  const { createOrUpdateRebateGroup } = useCreateOrUpdateRebateGroup({
    onCompleted: () => {
      refreshData()
      if (selectedGroup) {
        setSelectedGroup(undefined)
      }
    },
  })

  const { deleteRebateGroup, isLoading: isDeleteRebateGroupLoading } = useDeleteRebateGroup({
    onCompleted: refreshData,
  })

  const isTableLoading = tableQuery.isLoading || isDeleteRebateGroupLoading

  const handleGroupModalClose = () => {
    setGroupModalVisible(false)
    setSelectedGroup(undefined)
    setIsCreatingNewGroup(false)
  }

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    tableQuery.setPageQuery?.({
      ...tableQuery.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  const onEdit = (group: IntroducingBrokerRebateGroupDto) => {
    setSelectedGroup(group)
    setIsCreatingNewGroup(false)
    setGroupModalVisible(true)
  }

  const openCreateGroupModal = () => {
    setSelectedGroup(undefined)
    setIsCreatingNewGroup(true)
    setGroupModalVisible(true)
  }

  const onDelete = async (groupId: string) => {
    await deleteRebateGroup(groupId)
    setDeleteGroupId(undefined)
  }

  const handleSubmit = async (values: Partial<GroupFormValues>) => {
    if (
      values.name &&
      values.rebateAmountInPercentage &&
      values.rebateApprovalTypeId &&
      values.rebatePeriodId
    ) {
      await createOrUpdateRebateGroup(
        {
          name: values.name,
          rebateAmountInPercentage: values.rebateAmountInPercentage,
          rebateApprovalTypeId: values.rebateApprovalTypeId,
          rebatePeriodId: values.rebatePeriodId,
          isDefault: !!values.isDefault,
        },
        selectedGroup?.id
      )
    }

    refreshData()
    handleGroupModalClose()
  }

  return (
    <>
      {groupModalVisible && !isRebateApprovalTypeModalOpen && !isRebatePeriodModalOpen && (
        <Modal
          pullDown
          closeModal={() => handleGroupModalClose()}
          render={() => (
            <GroupModal
              closeModal={() => handleGroupModalClose()}
              groupData={selectedGroup}
              onSubmit={handleSubmit}
              isCreateGroup={isCreatingNewGroup}
              approvalTypes={rebateApprovalTypes}
              rebatePeriods={rebatePeriods}
              onOpenRebateApprovalTypeModal={() => {
                setRebateApprovalTypeModalOpen(true)
              }}
              onOpenRebatePeriodModal={() => {
                setRebatePeriodModalOpen(true)
              }}
              onSetSelectedGroup={(selectedGroup: Partial<IntroducingBrokerRebateGroupDto>) =>
                setSelectedGroup(selectedGroup)
              }
            />
          )}
        />
      )}
      {isRebateApprovalTypeModalOpen && (
        <RebateOptionsModal
          options={rebateApprovalTypes}
          activeId={selectedGroup?.rebateApprovalType?.id}
          onSelect={(option) => {
            setSelectedGroup({ ...selectedGroup, rebateApprovalType: option })
            setRebateApprovalTypeModalOpen(false)
          }}
          onClose={() => setRebateApprovalTypeModalOpen(false)}
        />
      )}
      {isRebatePeriodModalOpen && (
        <RebateOptionsModal
          options={rebatePeriods}
          activeId={selectedGroup?.rebatePeriod?.id}
          onSelect={(option) => {
            setSelectedGroup({ ...selectedGroup, rebatePeriod: option })
            setRebatePeriodModalOpen(false)
          }}
          onClose={() => setRebatePeriodModalOpen(false)}
        />
      )}
      {!!deleteGroupId?.length && (
        <Modal
          pullDown
          closeModal={() => setDeleteGroupId(undefined)}
          render={() => (
            <DeleteGroupModal
              onCancel={() => setDeleteGroupId(undefined)}
              onApply={async () => onDelete(deleteGroupId)}
            />
          )}
        />
      )}
      <div className={styles.groupsPage}>
        <RebatesSummary isLoading={isSummaryLoading} summaryItems={summaryItems} />

        <div className={styles.container}>
          <PageSection
            icon={FileIcon}
            title={t('IB.RebatesPage.Groups')}
            id={ScrollToIds.IBRebateGroupsHeader}
          >
            <HeaderActions
              onAddGroupClick={openCreateGroupModal}
              onFiltersClick={() => setIsFilterVisible((prevState) => !prevState)}
            />
          </PageSection>
          <TableFilter
            isVisible={isFilterVisible}
            searchFilters={tableSearch}
            groupSearchProps={{
              setSearch: (search) => search && setListSearch(search),
              options: groupOptions,
            }}
            filterOptions={[
              FilterOptions.IntroducingBrokerGroupId,
              FilterOptions.RebateApprovalType,
              FilterOptions.RebatePeriod,
              FilterOptions.TradingAccountCount,
              FilterOptions.Amount,
            ]}
            rebatePeriods={rebatePeriods}
            approvalTypes={rebateApprovalTypes}
            setSearchFilters={(searchFilters) => {
              if (!searchFilters) {
                return
              }
              setTableSearch(searchFilters)
              tableQuery.setPageQuery?.({
                ...tableQuery.pageQuery,
                pageIndex: 1,
              })
            }}
          />
          <div className={styles.tableContainer}>
            <Loading isLoading={isTableLoading} showLoadingIcon>
              {tableQuery.data.length ? (
                <GroupsPageTable
                  rebateGroups={tableQuery.data}
                  sortOptions={tableSortOptions}
                  pageQuery={tableQuery.pageQuery}
                  sticky='left'
                  setPageQuery={tableQuery.setPageQuery}
                  onEdit={(group) => onEdit(group)}
                  onDelete={(group) => setDeleteGroupId(group.id)}
                />
              ) : (
                <EmptyGroupsPageTable
                  onCreateClick={openCreateGroupModal}
                  isAnyFilterActive={isAnyFilterActive}
                />
              )}
              {tableQuery.meta && tableQuery.hasInitialResults && (
                <Pagination
                  scrollToHeaderId={ScrollToIds.IBRebateGroupsHeader}
                  maxPageSize={100}
                  pageData={tableQuery.meta}
                  isLoading={tableQuery.isLoading}
                  onPageChanged={handlePageChange}
                />
              )}
            </Loading>
          </div>
        </div>
      </div>
    </>
  )
}

interface IBRebateGroup {
  query: IAppendableFetchResult<IntroducingBrokerRebateGroupDto[]>
  sortOptions: NameDto<keyof IntroducingBrokerRebateGroupDto | null>[]
  search?: FilterQueryProps
  setSearch: (search: FilterQueryProps) => void
  forceRefresh: () => void
  clearSearch: (callback: () => void) => void
}

const useIBRebateGroups = (): IBRebateGroup => {
  const { t } = useTranslation()

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()

  const [search, setSearch] = useState<FilterQueryProps>()

  const sortOptions: NameDto<keyof IntroducingBrokerRebateGroupDto | null>[] = [
    {
      id: 'name',
      name: t('IB.RebatesPage.GroupsPage.Group name'),
    },
    {
      id: 'rebateApprovalType',
      name: t('IB.RebatesPage.GroupsPage.Group type'),
    },
    {
      id: 'rebatePeriod',
      name: t('IB.RebatesPage.GroupsPage.Rebate period'),
    },
    {
      id: null,
      name: t('IB.RebatesPage.GroupsPage.TAs'),
    },
    {
      id: 'rebateAmountInPercentage',
      name: t('IB.RebatesPage.GroupsPage.Rebate'),
    },
  ]

  const clearSearch = (callback: () => void) => {
    callback()
    setSearch({})
  }

  const { callback: accountSummaryCallback, forceRefresh } = useCallbackWithForceRefresh(
    async (query?: PageQuery) => {
      return await apiClient.getIBRebateGroups(account?.clientIntroducingBroker?.id ?? '', {
        ...query,
        search: search,
        caller: 'ca',
        languageId: locale,
      })
    },
    [search, locale]
  )
  const query = useFetchAppendablePage(accountSummaryCallback)

  return {
    query,
    sortOptions,
    search,
    setSearch,
    forceRefresh,
    clearSearch,
  }
}

interface IBRebateGroupsSummary {
  summaryItems: SummaryItem[]
  forceRefresh: () => void
  isLoading: boolean
}

const useIBRebateGroupsSummary = (search?: FilterQueryProps): IBRebateGroupsSummary => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const { t } = useTranslation()

  const { callback, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!account?.clientIntroducingBroker?.id) {
      return null
    }

    const rebateGroupsSummary = await apiClient.getIBRebateGroupsSummary(
      account.clientIntroducingBroker.id,
      { search }
    )

    return rebateGroupsSummary
  }, [account?.clientIntroducingBroker?.id, apiClient, search])

  const { data: summaryData, isLoading } = useFetchOne(callback)

  const summaryItems = useMemo<SummaryItem[]>(() => {
    return [
      {
        title: t('IB.RebatesPage.GroupsPage.Groups total'),
        value: summaryData?.groupsCount || 0,
      },
      {
        title: t('IB.RebatesPage.GroupsPage.TAs in groups'),
        value: summaryData?.tasInGroups || 0,
      },
      {
        title: t('IB.RebatesPage.GroupsPage.TAs with manual approval'),
        value: summaryData?.tasWithManualApproval || 0,
      },
      {
        title: t('IB.RebatesPage.GroupsPage.TAs not in a group'),
        value: summaryData?.tasNotInGroup || 0,
      },
    ]
  }, [summaryData, t])

  return { summaryItems, forceRefresh, isLoading }
}

interface DeleteRebateGroup {
  isLoading: boolean
  deleteRebateGroup: (id: string) => Promise<void>
}

interface DeleteRebateGroupParams {
  onCompleted?: () => void
}

const useDeleteRebateGroup = ({ onCompleted }: DeleteRebateGroupParams = {}): DeleteRebateGroup => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const { addSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState(false)

  const deleteRebateGroup = async (groupId: string) => {
    if (!account?.clientIntroducingBroker?.id) {
      return
    }

    setIsLoading(true)

    try {
      await apiClient.deleteRebateGroup(account.clientIntroducingBroker.id, groupId)
      addSnackbar.success({
        message: t('IB.RebatesPage.GroupsPage.You’ve successfully deleted a rebate group'),
      })

      setIsLoading(false)
      onCompleted?.()
    } catch (error: unknown) {
      setIsLoading(false)
    }
  }

  return { deleteRebateGroup, isLoading }
}
